/**
 * Settings service — live from W11. Three things, all real endpoints:
 *
 *   the firm           GET /firm · PATCH /firm {name, contact_email}  (PATCH: main account)
 *   national registry  GET /firm/national-sources
 *   connections        PUT /firm/national-sources/:cc {kind, connection_config, secret_ref, active}
 *   (D-014)            DELETE /firm/national-sources/:cc              (PUT/DELETE: main account)
 *   your own account   GET /users/:id · PATCH /users/:id {display_name, locale}
 *                      PATCH /users/:id {password, current_password}
 *
 * The mockup's settings tree (`GET/PATCH /settings` with AI sensitivity,
 * security policy, theme and density) had no endpoint and never will in that
 * shape: the matcher's tuning is platform-wide (the operator's console), and
 * sign-in security arrives with production auth. It is gone, with its fixture.
 *
 * **Credentials are never sent from here.** The API refuses a password, token
 * or key inside `connection_config`; a credential lives in the server's
 * environment and the connection names it in `secret_ref` — which the platform
 * operator sets. This form asks for neither, and an edit sends the stored
 * `secret_ref` back unchanged: the PUT replaces the whole row, so leaving it
 * out would quietly unlink the credential the operator set up.
 */
import { apiResolve, http, HttpError } from '@/api'
import { isLive } from '@/api/config'
import type { Locale } from '@/i18n'
import users from '@/mock/users.json'
import type { ServiceOptions } from './types'

const LIVE = () => isLive('settings')

/* ═════════════════════════════════════════════════════════ API shapes */

export interface ApiFirm {
  id: number
  name: string
  contact_email: string | null
  status: string
  created_at: string
}

export interface ApiNationalSource {
  country_code: string
  kind: 'api' | 'ftp'
  connection_config: Record<string, unknown>
  secret_ref: string | null
  active: boolean
  adapter_available: boolean
  updated_at: string
}

export interface ApiUser {
  id: number | string
  firm_id: number | null
  user_kind: string
  email: string
  display_name: string
  lawyer_role: string | null
  active: boolean
  locale: string | null
  created_at: string
}

/* ═════════════════════════════════════════════════════════ SPA shapes */

export interface Firm {
  id: number
  name: string
  contactEmail: string | null
}

export interface NationalSource {
  countryCode: string
  kind: 'api' | 'ftp'
  /** Where the registry feed lives — the one setting this form edits. */
  address: string
  /** Every stored setting, the address included, kept so an edit loses none. */
  connectionConfig: Record<string, unknown>
  /** The *name* of the server variable holding the credential, or null. */
  secretRef: string | null
  active: boolean
  /** Whether the platform can read this registry at all yet (D-033). */
  adapterAvailable: boolean
  updatedAt: string
}

export interface NationalSourceInput {
  countryCode: string
  kind: 'api' | 'ftp'
  address: string
  active: boolean
}

export interface Profile {
  id: number | string
  email: string
  displayName: string
  userKind: string
  lawyerRole: string | null
  /** The language emails are written in; null = never chosen (English). */
  locale: Locale | null
}

/** The key the address is stored under in `connection_config`. */
export const ADDRESS_KEY = 'url'

/* ═════════════════════════════════════════════════════════ adapters */

export function firmFromApi(f: ApiFirm): Firm {
  return { id: f.id, name: f.name, contactEmail: f.contact_email ?? null }
}

export function sourceFromApi(s: ApiNationalSource): NationalSource {
  const config = { ...(s.connection_config ?? {}) }
  const address = config[ADDRESS_KEY]
  return {
    countryCode: s.country_code,
    kind: s.kind,
    address: typeof address === 'string' ? address : '',
    connectionConfig: config,
    secretRef: s.secret_ref ?? null,
    active: !!s.active,
    adapterAvailable: !!s.adapter_available,
    updatedAt: s.updated_at,
  }
}

/** Settings stored on a connection other than the address, for display. */
export function otherSettings(source: NationalSource): string[] {
  return Object.keys(source.connectionConfig).filter((k) => k !== ADDRESS_KEY)
}

/**
 * The PUT body for a connection. Carries over everything the form does not
 * edit — other settings and, above all, the operator's `secret_ref`.
 */
export function sourceBody(
  input: NationalSourceInput,
  existing: NationalSource | null = null
): Record<string, unknown> {
  const config: Record<string, unknown> = { ...(existing?.connectionConfig ?? {}) }
  const address = input.address.trim()
  if (address) config[ADDRESS_KEY] = address
  else delete config[ADDRESS_KEY]
  return {
    kind: input.kind,
    connection_config: config,
    secret_ref: existing?.secretRef ?? null,
    active: input.active,
  }
}

export function normaliseCountry(code: string): string {
  return code.trim().toUpperCase()
}

export function isCountryCode(code: string): boolean {
  return /^[A-Z]{2}$/.test(normaliseCountry(code))
}

const LOCALES = ['en', 'bg', 'fr', 'de']

export function profileFromApi(u: ApiUser): Profile {
  return {
    id: u.id,
    email: u.email,
    displayName: u.display_name,
    userKind: u.user_kind,
    lawyerRole: u.lawyer_role ?? null,
    locale: u.locale && LOCALES.includes(u.locale) ? (u.locale as Locale) : null,
  }
}

/** A 403 on a password change: the current password was wrong. */
export function isWrongPassword(err: unknown): boolean {
  return err instanceof HttpError && err.status === 403
}

/* ═════════════════════════════════════════════════════════ mock */

interface MockDb {
  firm: ApiFirm
  sources: ApiNationalSource[]
  profile: ApiUser | null
}
let mockDb: MockDb | null = null

function db(): MockDb {
  if (!mockDb) {
    mockDb = {
      firm: {
        id: 1,
        name: 'QuickFox Legal',
        contact_email: 'office@quickfox.example',
        status: 'active',
        created_at: '2026-08-15T09:00:00Z',
      },
      sources: [
        {
          country_code: 'BG',
          kind: 'ftp',
          connection_config: { url: 'ftp.registry.example' },
          secret_ref: 'LEX_BG_FTP_CREDENTIALS',
          active: true,
          adapter_available: false,
          updated_at: '2026-09-20T08:30:00Z',
        },
      ],
      profile: null,
    }
  }
  return mockDb
}

function mockProfile(id: number | string): ApiUser {
  const store = db()
  if (store.profile && String(store.profile.id) === String(id)) return store.profile
  const u = users.find((x) => String(x.id) === String(id)) ?? users[0]!
  store.profile = {
    id: u.id,
    firm_id: 1,
    user_kind: (u as { userKind?: string }).userKind ?? 'firm_main',
    email: u.email,
    display_name: u.name,
    lawyer_role: null,
    active: true,
    locale: null,
    created_at: '2026-08-15T09:00:00Z',
  }
  return store.profile
}

/** Test hook. */
export function resetMockSettings(): void {
  mockDb = null
}

const opts = (signal: AbortSignal | null | undefined, path: string, method = 'GET') => ({
  signal,
  path,
  method,
})

/* ═════════════════════════════════════════════════════════ the service */

const service = {
  async firm({ signal }: ServiceOptions = {}): Promise<Firm> {
    if (!LIVE()) return firmFromApi(await apiResolve(db().firm, opts(signal, '/firm')))
    return firmFromApi(await http.get('/firm', { signal }))
  },

  async updateFirm(
    patch: { name?: string; contactEmail?: string | null },
    { signal }: ServiceOptions = {}
  ): Promise<Firm> {
    const body: Record<string, unknown> = {}
    if (patch.name !== undefined) body.name = patch.name.trim()
    if (patch.contactEmail !== undefined) body.contact_email = patch.contactEmail?.trim() || null
    if (!LIVE()) {
      await apiResolve(null, opts(signal, '/firm', 'PATCH'))
      Object.assign(db().firm, body)
      return firmFromApi(db().firm)
    }
    return firmFromApi(await http.patch('/firm', body, { signal }))
  },

  async nationalSources({ signal }: ServiceOptions = {}): Promise<NationalSource[]> {
    if (!LIVE()) {
      const rows = await apiResolve(db().sources, opts(signal, '/firm/national-sources'))
      return rows.map(sourceFromApi)
    }
    const rows: ApiNationalSource[] = await http.get('/firm/national-sources', { signal })
    return (rows ?? []).map(sourceFromApi)
  },

  async saveNationalSource(
    input: NationalSourceInput,
    existing: NationalSource | null = null,
    { signal }: ServiceOptions = {}
  ): Promise<NationalSource> {
    const code = normaliseCountry(input.countryCode)
    const body = sourceBody(input, existing)
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/firm/national-sources/${code}`, 'PUT'))
      const store = db()
      const row: ApiNationalSource = {
        country_code: code,
        kind: body.kind as 'api' | 'ftp',
        connection_config: body.connection_config as Record<string, unknown>,
        secret_ref: body.secret_ref as string | null,
        active: body.active as boolean,
        adapter_available: false,
        updated_at: new Date().toISOString(),
      }
      store.sources = [...store.sources.filter((s) => s.country_code !== code), row].sort((a, b) =>
        a.country_code.localeCompare(b.country_code)
      )
      return sourceFromApi(row)
    }
    return sourceFromApi(await http.put(`/firm/national-sources/${code}`, body, { signal }))
  },

  async removeNationalSource(countryCode: string, { signal }: ServiceOptions = {}): Promise<void> {
    const code = normaliseCountry(countryCode)
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/firm/national-sources/${code}`, 'DELETE'))
      const store = db()
      if (!store.sources.some((s) => s.country_code === code)) {
        throw new HttpError(404, 'National source not found')
      }
      store.sources = store.sources.filter((s) => s.country_code !== code)
      return
    }
    await http.delete(`/firm/national-sources/${code}`, { signal })
  },

  /** The signed-in person's own record — the one place `locale` is readable. */
  async profile(userId: number | string, { signal }: ServiceOptions = {}): Promise<Profile> {
    if (!LIVE())
      return profileFromApi(await apiResolve(mockProfile(userId), opts(signal, '/users')))
    return profileFromApi(await http.get(`/users/${userId}`, { signal }))
  },

  async updateProfile(
    userId: number | string,
    patch: { displayName?: string; locale?: Locale },
    { signal }: ServiceOptions = {}
  ): Promise<Profile> {
    const body: Record<string, unknown> = {}
    if (patch.displayName !== undefined) body.display_name = patch.displayName.trim()
    if (patch.locale !== undefined) body.locale = patch.locale
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/users/${userId}`, 'PATCH'))
      return profileFromApi(Object.assign(mockProfile(userId), body))
    }
    return profileFromApi(await http.patch(`/users/${userId}`, body, { signal }))
  },

  /**
   * Change one's own password. The API wants the current one too — a borrowed
   * session must not be able to take the account for good — and answers 403
   * when it is wrong (`isWrongPassword`).
   */
  async changePassword(
    userId: number | string,
    current: string,
    next: string,
    { signal }: ServiceOptions = {}
  ): Promise<void> {
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/users/${userId}`, 'PATCH'))
      if (!current) throw new HttpError(403, 'Your current password is required to set a new one')
      return
    }
    /* A 403 here means the current password was wrong; the refresh
     * interceptor acts on 401 only, so it is not mistaken for a dead session. */
    await http.patch(`/users/${userId}`, { password: next, current_password: current }, { signal })
  },
}

export default service
